import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
  Divider,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Badge,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { API } from "../../utils/Api";
import axios from "axios";

/* ==================== Theme tokens (from your SCSS) ==================== */
const T = {
  primary: "#09A5F7",
  secondary: "#DFEEF4",
  text: "#0E4961",
  green: "#36CD1D",
  orange: "#FB9000",
  properties: "#D0EFFF",
  white: "#fff",
  black: "#000",
  subText: "#6a6a6a",
};

/* ==================== Utils ==================== */

const formatDay = (iso) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(iso ? new Date(iso) : new Date());

const formatTime24 = (iso) =>
  new Intl.DateTimeFormat(undefined, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(iso ? new Date(iso) : new Date());

/* ==================== MessageBubble ==================== */

function MessageBubble({ mine, text, at, role }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent={mine ? "flex-end" : "flex-start"}
      spacing={1}
    >
      {!mine && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: role === "ADMIN" ? T.primary : T.orange,
            fontSize: 12,
          }}
        >
          {role === "ADMIN" ? "A" : "U"}
        </Avatar>
      )}

      <Paper
        elevation={0}
        sx={{
          maxWidth: { xs: "85%", sm: "70%" },
          px: 1.5,
          py: 1.125,
          borderRadius: mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          bgcolor: mine ? "#E8F9F0" : T.white,
          border: "1px solid",
          borderColor: mine ? "#cbeedd" : "#eee",
        }}
      >
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, display: "block", mb: 0.25, color: T.subText }}
        >
          {role === "ADMIN" ? "Admin" : "You"} · {formatTime24(at)}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: T.text }}>
          {text}
        </Typography>
      </Paper>
    </Stack>
  );
}

function ChatThread({ ticketId, apiBase, authHeaders, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const scrollerRef = useRef(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${apiBase}/tickets/${ticketId}/messages`, {
          headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        });
        const data = await res.json();
        if (!abort) {
          const sorted = [...data].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          setMessages(sorted);
        }
      } catch {
        // optional toast
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [ticketId, apiBase, authHeaders]);

  // auto scroll to bottom on new messages
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);

    const optimistic = {
      id: `tmp-${Date.now()}`,
      ticketId,
      senderId: currentUserId,
      senderRole: "USER",
      text: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setText("");

    try {
      const res = await fetch(`${apiBase}/tickets/${ticketId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({ text: trimmed }),
      });
      const saved = await res.json();
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? saved : x)));
    } catch {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setText(trimmed);
    } finally {
      setSending(false);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const msg of messages) {
      const dayKey = new Date(msg.createdAt);
      const key = `${dayKey.getFullYear()}-${dayKey.getMonth()}-${dayKey.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(msg);
    }
    return Array.from(map.entries()).map(([key, list]) => ({
      dayLabel: formatDay(list[0]?.createdAt),
      list,
    }));
  }, [messages]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* thread */}
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          bgcolor: { xs: "#F7FBFF", sm: "#f9fbfd" },
          backgroundImage: {
            md: `linear-gradient(180deg, ${T.secondary} 0%, #ffffff 30%)`,
          },
        }}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : messages.length === 0 ? (
          <Stack alignItems="center" spacing={1.25} sx={{ pt: 6, color: T.subText }}>
            <Typography variant="subtitle1" fontWeight={600}>
              No messages yet
            </Typography>
            <Typography variant="body2">Say hello to start the conversation.</Typography>
          </Stack>
        ) : (
          grouped.map((g, idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              <Divider>
                <Chip
                  label={g.dayLabel}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: T.white, borderColor: T.secondary, color: T.subText }}
                />
              </Divider>
              <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                {g.list.map((m) => {
                  const mine = String(m.senderId) === String(currentUserId);
                  return (
                    <MessageBubble
                      key={m.id}
                      mine={mine}
                      text={m.text}
                      at={m.createdAt}
                      role={m.senderRole}
                    />
                  );
                })}
              </Stack>
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          borderTop: "1px solid #eee",
          p: 1,
          bgcolor: T.white,
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          // safe-area on iOS
          pb: "calc(8px + env(safe-area-inset-bottom))",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Type your message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={1}
          maxRows={5}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "#FAFCFF",
              borderRadius: 2,
            },
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={sending || !text.trim()}
              sx={{
                bgcolor: T.primary,
                color: T.white,
                "&:hover": { bgcolor: "#0793dc" },
                borderRadius: 2,
                width: 44,
                height: 40,
              }}
            >
              {sending ? <CircularProgress size={22} sx={{ color: T.white }} /> : <SendIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

const StatusChip = ({ status }) => {
  const map = {
    OPEN: { color: "success", bg: "#E9F9EE", text: "#1F7A3D", border: "#BFEAD0" },
    IN_PROGRESS: { color: "warning", bg: "#FFF7E8", text: "#8A5A00", border: "#FFE2B3" },
    ESCALATED: { color: "error", bg: "#FDEBEC", text: "#9B1C1F", border: "#FACDD1" },
    CLOSED: { color: "default", bg: "#F2F4F7", text: "#4B5563", border: "#E5E7EB" },
  };
  const tone = map[status] || map.OPEN;
  return (
    <Chip
      label={status}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 700,
        bgcolor: tone.bg,
        color: tone.text,
        borderColor: tone.border,
        height: 26,
      }}
    />
  );
};


export default function SupportTickets({
  userId,
  tickets = [],
  isSmallScreen,
  apiBase = "/api",
  authHeaders = async () => ({}),
}) {
  const theme = useTheme();
  const token = localStorage.getItem("token");
  const [conversation, setConversation] = useState();
  const mediaMatch = useMediaQuery(theme.breakpoints.down("md"));
  const small = typeof isSmallScreen === "boolean" ? isSmallScreen : mediaMatch;

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openTicketModal, setOpenTicketModal] = useState(false);

  const handleOpenTicket = (ticket) => {
    setSelectedTicket(ticket);
    setOpenTicketModal(true);
  };
  const handleCloseTicketModal = () => {
    setSelectedTicket(null);
    setOpenTicketModal(false);
  };

  const getConversation = async (userId) => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };
      const response = await axios.get(`${API.BASE_URL}customer-support/${userId}/conversation`, { headers });
      const ticketData = response?.data?.data?.tickets;
    } catch (error) {
      if (error.response && error.response?.data?.status === false) {
        let errorMessage = error.response.data.error;
      }
    }
  };

  useEffect(() => {
    getConversation();
  },[])

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 4 },
        py: { xs: 1.5, md: 4 },
        minHeight: "100vh",
        bgcolor: { xs: T.white, md: "#F4F9FF" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: T.text,
            letterSpacing: 0.2,
          }}
          gutterBottom
        >
          Tickets
        </Typography>

        <Badge
          badgeContent={tickets.length}
          color="primary"
          sx={{
            "& .MuiBadge-badge": {
              bgcolor: T.primary,
              color: T.white,
            },
          }}
        >
          <Box />
        </Badge>
      </Box>

      {tickets.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            p: { xs: 3, md: 4 },
            width: "100%",
            maxWidth: 900,
            borderRadius: 3,
            border: "1px dashed #cfe8ff",
            bgcolor: "#F8FBFF",
          }}
        >
          <Typography variant="h6" sx={{ color: "#335B73", fontWeight: 700 }}>
            No Tickets Raised
          </Typography>
          <Typography variant="body2" sx={{ color: T.subText }}>
            If you need assistance, please raise a ticket and our team will help you.
          </Typography>
        </Paper>
      ) : small ? (
        /* mobile list */
        <List sx={{ width: "100%", maxWidth: 900 }}>
          {tickets.map((ticket) => (
            <ListItemButton
              key={ticket.id}
              onClick={() => handleOpenTicket(ticket)}
              sx={{
                border: "1px solid #E6EEF6",
                borderRadius: 2,
                mb: 1.2,
                p: 1.25,
                bgcolor: T.white,
                boxShadow: "0 1px 0 rgba(16,24,40,0.02)",
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: T.text }}>
                      #{ticket.ticket_no}
                    </Typography>
                    <StatusChip status={ticket.status} />
                  </Stack>
                }
                secondary={
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    <Typography component="span" variant="body2" sx={{ color: T.subText }}>
                      {ticket.subject}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                      <Chip
                        size="small"
                        label={ticket.support_type}
                        sx={{
                          height: 22,
                          bgcolor: T.secondary,
                          color: T.text,
                          borderRadius: 1.5,
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#7A8A96" }}>
                        {ticket.name} · {ticket.email}
                      </Typography>
                    </Stack>
                  </Stack>
                }
              />
            </ListItemButton>
          ))}
        </List>
      ) : (
        /* desktop table */
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            width: "100%",
            maxWidth: 1400,
            overflowX: "auto",
            boxShadow: "0 6px 20px rgba(9,165,247,0.08)",
            border: "1px solid #E6EEF6",
            background:
              "linear-gradient(180deg, rgba(223,238,244,0.35) 0%, rgba(255,255,255,1) 40%)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Ticket No", "Name", "Email", "Support Type", "Subject", "Message", "Status"].map(
                  (head) => (
                    <TableCell
                      key={head}
                      align="center"
                      sx={{ fontWeight: 800, color: T.text, bgcolor: "#F2F8FE" }}
                    >
                      {head}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F7FBFF" },
                  }}
                  onClick={() => handleOpenTicket(ticket)}
                >
                  <TableCell align="center">#{ticket.ticket_no}</TableCell>
                  <TableCell align="center">{ticket.name}</TableCell>
                  <TableCell align="center">{ticket.email}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={ticket.support_type}
                      sx={{ bgcolor: T.properties, color: T.text, height: 22 }}
                    />
                  </TableCell>
                  <TableCell align="center">{ticket.subject}</TableCell>
                  <TableCell align="center" sx={{ maxWidth: 360 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: "#334155",
                      }}
                    >
                      {ticket.message}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip status={ticket.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ticket details + conversation modal */}
      <Dialog
        open={openTicketModal}
        onClose={handleCloseTicketModal}
        fullWidth
        maxWidth="md"
        fullScreen={small}
        PaperProps={{
          sx: {
            borderRadius: small ? 0 : 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: T.text,
            color: "#fff",
            fontWeight: 800,
            py: 1.25,
          }}
        >
          {selectedTicket ? `Ticket #${selectedTicket.ticket_no}` : "Ticket Details"}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {selectedTicket && (
            <Box sx={{ display: "flex", flexDirection: "column", height: small ? "100vh" : 620 }}>
              {/* header/details */}
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: "#F8FBFF",
                  borderBottom: "1px solid #E6EEF6",
                }}
              >
                <DialogContentText component="div">
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body1" sx={{ color: T.text }}>
                        <strong>Subject:</strong> {selectedTicket.subject}
                      </Typography>
                      <Typography variant="body2" sx={{ color: T.subText }}>
                        {selectedTicket.name} · {selectedTicket.email}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={selectedTicket.support_type}
                        sx={{ bgcolor: T.secondary, color: T.text }}
                      />
                      <StatusChip status={selectedTicket.status} />
                    </Stack>
                  </Stack>
                </DialogContentText>
              </Box>

              {/* conversation */}
              <ChatThread
                key={selectedTicket.id}
                ticketId={selectedTicket.id}
                apiBase={apiBase}
                authHeaders={authHeaders}
                currentUserId={userId}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: "#F8FBFF" }}>
          <Button
            onClick={handleCloseTicketModal}
            variant="outlined"
            sx={{
              color: T.text,
              borderColor: "#C9E7F9",
              fontWeight: 700,
              "&:hover": { borderColor: T.primary, bgcolor: "#F2F9FF" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
